import SideNavigation, { SideNavigationProps } from '@amzn/awsui-components-core/side-navigation';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { logger } from 'src/logger';
import { ApplicationFlatTableEntity } from '../context/AppContextModels';
import { useAppContext } from '../context/AppContextProvider';
import { UserActionsEventTypes } from '../das-finsuite/das-finsuite-constants';
import { useAuth } from '../context/AuthContextProvider';
import { ENVIRONMENT_VARIABLES } from '../../constants/EnvironmentConstants';
import {
  getFinSuiteNavigationItems,
  getNavigationItemsBySection,
  getOperationalExcellenceNavigationItems,
  ROUTE_CONFIG
} from '../../utilities/RouteConfig';

import './HomePageSideNavigationV2.scss';

/**
 * Builds the full SideNavigationProps.Item[] for the Cloudscape SideNavigation component.
 */
const buildNavigationItems = (
  userLDAPGroups: string[] | undefined,
  stage: string,
  homePageContent: ReturnType<typeof useAppContext>['homePageContent'],
  favoriteApps: ApplicationFlatTableEntity[]
): SideNavigationProps.Item[] => {
  const items: SideNavigationProps.Item[] = [];

  // section-group only accepts Link | Section | LinkGroup | ExpandableLinkGroup (not Divider)
  const linksOnly = (arr: SideNavigationProps.Item[]): SideNavigationProps.Link[] =>
    arr.filter((i): i is SideNavigationProps.Link => i.type === 'link');

  // FinSuite top-level links (View All Apps, Product Launches, etc.)
  items.push(...getFinSuiteNavigationItems(userLDAPGroups, stage));

  // Org Tools section
  const orgToolsItems = linksOnly(getNavigationItemsBySection('org-tools', userLDAPGroups, stage));
  if (orgToolsItems.length > 0) {
    items.push({ type: 'divider' }, { type: 'section-group', title: 'Org Tools', items: orgToolsItems });
  }

  // Kingpin section
  const kingpinItems = linksOnly(getNavigationItemsBySection('kingpin'));
  if (kingpinItems.length > 0) {
    items.push({ type: 'divider' }, { type: 'section-group', title: 'Kingpin', items: kingpinItems });
  }

  // Operational Excellence section — use section-group for consistent styling,
  // theme support, and always-visible children. The title is made clickable via useEffect.
  const oeConfig = ROUTE_CONFIG['/operational-excellence'];
  const oeChildItems = linksOnly(getOperationalExcellenceNavigationItems());
  items.push(
    { type: 'divider' },
    {
      type: 'section-group',
      title: oeConfig.navigationText || oeConfig.breadcrumbText,
      items: oeChildItems
    }
  );

  // Support links from homepage content
  const supportLinks = homePageContent?.sidePanelDetails?.supportLinks;
  if (supportLinks?.isEnabled && supportLinks?.tableContent && supportLinks.tableContent.length > 0) {
    items.push({ type: 'divider' });
    supportLinks.tableContent
      .sort((a, b) => a.displayName.localeCompare(b.displayName))
      .forEach((link) => {
        items.push({ type: 'link', text: link.displayName, href: link.link, external: true });
      });
  }

  // My Favorites section
  if (favoriteApps.length > 0) {
    items.push(
      { type: 'divider' },
      {
        type: 'section-group',
        title: 'My Favorites',
        items: favoriteApps.map((app) => ({
          type: 'link' as const,
          text: app.applicationName,
          href: app.embeddingEnabled ? `/all-apps/${app.id}` : app.applicationLink || '#',
          external: !app.embeddingEnabled && !!app.applicationLink
        }))
      }
    );
  }

  return items;
};

export const HomePageSideNavigationV2: React.FC = () => {
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();
  const userAuthenticationDetails = useAuth();
  const { favoriteApplications, parsedApplications, homePageContent } = useAppContext();
  const [activeHref, setActiveHref] = useState('');
  const navRef = useRef<HTMLDivElement>(null);

  const favoriteAppsList = useMemo<ApplicationFlatTableEntity[]>(() => {
    const activeParsedApps = parsedApplications.filter((app) => app.isActive);
    return activeParsedApps.filter((app) => favoriteApplications.includes(app.id));
  }, [favoriteApplications, parsedApplications]);

  useEffect(() => {
    setActiveHref(pathname + hash);
  }, [pathname, hash]);

  const items = useMemo(
    () => buildNavigationItems(userAuthenticationDetails.userLDAPGroups, ENVIRONMENT_VARIABLES.env.Stage, homePageContent, favoriteAppsList),
    [userAuthenticationDetails.userLDAPGroups, homePageContent, favoriteAppsList]
  );

  const handleFollow: SideNavigationProps['onFollow'] = (event) => {
    event.preventDefault();
    const { href, text, external } = event.detail;

    logger.info('DaS FinSuite -> User Action: FinSuite Home page side nav', {
      event_type: UserActionsEventTypes.HOME_PAGE_SIDE_NAV_CLICK,
      value: text
    });

    if (external || href.startsWith('http://') || href.startsWith('https://')) {
      window.open(href, '_blank', 'noopener,noreferrer');
      return;
    }

    navigate(href, {
      state: { from: '/', redirectFromHomePage: true, fromAppName: '', fromAppId: '' }
    });
  };

  // Make the "Operational Excellence" section-group title clickable.
  // section-group renders a plain <span> for the title — we add a click handler
  // and a CSS class for the hover underline affordance.
  useEffect(() => {
    if (!navRef.current) return;

    const oeTitle = ROUTE_CONFIG['/operational-excellence'].navigationText || ROUTE_CONFIG['/operational-excellence'].breadcrumbText;

    // Find all section-group title elements and match by text content
    const titleElements = navRef.current.querySelectorAll<HTMLElement>('[class*="section-group-title"]');
    let targetTitle: HTMLElement | null = null;

    titleElements.forEach((el) => {
      if (el.textContent?.trim() === oeTitle) {
        targetTitle = el;
      }
    });

    if (!targetTitle) return;

    const titleEl = targetTitle as HTMLElement;
    titleEl.classList.add('oe-clickable-title');
    titleEl.setAttribute('role', 'link');
    titleEl.setAttribute('tabindex', '0');

    const handleClick = () => {
      navigate('/operational-excellence', {
        state: { from: '/', redirectFromHomePage: true, fromAppName: '', fromAppId: '' }
      });
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleClick();
      }
    };

    titleEl.addEventListener('click', handleClick);
    titleEl.addEventListener('keydown', handleKeyDown);
    return () => {
      titleEl.removeEventListener('click', handleClick);
      titleEl.removeEventListener('keydown', handleKeyDown);
      titleEl.removeAttribute('role');
      titleEl.removeAttribute('tabindex');
      titleEl.classList.remove('oe-clickable-title');
    };
  }, [items, navigate]);

  return (
    <div className="compact-side-nav" ref={navRef}>
      <SideNavigation activeHref={activeHref} items={items} onFollow={handleFollow} />
    </div>
  );
};
